import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import DisplayMembers from './components/DisplayMembers';
import { USERSROLE } from 'types';
import { MainContainer, MainHeaderContainer } from 'layouts/components';
import {
  fetchOrgTenantsStart,
  updateNewRecordAddedFlag,
  updateOrgProfileUpdateFlag
} from 'redux/member/action';
import {
  selectIsNewRecordAdded,
  selectMemberUpdated,
  selectOrganisationTenants
} from 'redux/member';
import { APP } from 'app/AppRoutes';
import {
  ToastNotification,
  InlineNotification,
  Loading
} from 'carbon-components-react';
import { selectMemberLoading } from 'redux/members/selector';
import {
  selectedMembers,
  selectedError,
  clearSelectedMember,
  fetchMemberStart,
  selectPaginationInfo,
  removeErrors
} from 'redux/members';

import {
  selectErrorMessage,
  removeErrors as removeErrorsFromAddMember
} from 'redux/member';
import AddMemberForm from './components/forms/AddMemberForm';
import { selectIsModalOpen } from 'redux/member';
import { currentUser } from 'redux/session/selectors';
import { InlineToastAlerts } from 'components';
import { useComponentMount } from 'utilities/hooks.util';

const Members: React.FC<any> = () => {
  const dispatch: any = useDispatch();
  const ref = useRef(null);
  // const history: any = useHistory();
  const initialPaginationProps = {
    offset: 0,
    limit: 10
  };
  const [paginationProps, setPaginationProps] = useState(
    initialPaginationProps
  );
  const [sortOrder, setSortOrder] = useState<any>({
    sortedBy: 'name',
    sortedOrder: 'A'
  });
  const [filterBy, setFilterBy] = useState<any>('role:ASSETOWNER');
  const paginationInfo: any = useSelector(selectPaginationInfo);
  const isModalOpen: boolean = useSelector(selectIsModalOpen);
  const errorMessage: string = useSelector(selectedError);
  const errorMessageAddMember: string = useSelector(selectErrorMessage);
  const isNewRecordedAdded = useSelector(selectIsNewRecordAdded);
  const memberUpdated = useSelector(selectMemberUpdated);
  const orgTenants = useSelector(selectOrganisationTenants);
  const user = useSelector(currentUser);

  const handleFetchNextPage = (props: any) => {
    const { page, pageSize } = props;
    setPaginationProps({
      offset: page * pageSize - pageSize,
      limit: pageSize
    });
  };
  const handleChangeSearchBy = (searchText: string | null) => {
    setPaginationProps(initialPaginationProps);
    if (searchText) {
      setFilterBy(`${filterBy},name:"%${searchText}%"`);
    } else {
      setFilterBy(`role:ASSETOWNER`);
    }
  };
  const pageInfo = {
    currentPage: paginationInfo.offset / paginationInfo.limit + 1,
    pageSize: paginationInfo.limit,
    totalItems: paginationInfo.count,
    handleFetchNextPage
  };
  const members: Array<any> = useSelector(selectedMembers);

  const membersWithSelectedFields = members?.map((item: any) => ({
    id: item.id.toString(),
    name: item.name,
    utilityId: item.utilityId,
    organisationType: item.organisationType,
    tenant: item.Tenant?.name?.replace('DBYD ', ''),
    isDisabled: item.isDisabled
  }));
  const handleRemoveError = () => {
    errorMessage && dispatch(removeErrors());
    errorMessageAddMember && dispatch(removeErrorsFromAddMember());
  };

  const [params, setParams] = useState<any>({});
  useEffect(() => {
    const localParams: any = {
      fields: 'id,name,PrimaryContact,utilityId,organisationType,isDisabled',
      order: `${sortOrder.sortedOrder === 'A' ? '' : '-'}${sortOrder.sortedBy}`,
      limit: paginationProps.limit,
      offset: paginationProps.offset,
      filter: filterBy,
      include: 'Tenant'
    };
    setParams(localParams);
    dispatch(fetchMemberStart(localParams));
  }, [paginationProps, filterBy, sortOrder, dispatch]);

  useEffect(() => {
    const element: any = ref.current;

    if (element) {
      // This is to ensure all element that can be tab, tabindex to -1
      Array.from(element.getElementsByTagName('button')).forEach(
        (item: any) => {
          item.tabIndex = isModalOpen ? -1 : 0;
        }
      );

      Array.from(element.getElementsByTagName('input')).forEach((item: any) => {
        item.tabIndex = isModalOpen ? -1 : 0;
      });

      Array.from(element.getElementsByTagName('a')).forEach((item: any) => {
        item.tabIndex = isModalOpen ? -1 : 0;
      });

      Array.from(element.getElementsByTagName('select')).forEach(
        (item: any) => {
          item.tabIndex = isModalOpen ? -1 : 0;
        }
      );
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (
      user.role === USERSROLE.DBYDADMIN &&
      !user?.Organisation?.tenantId &&
      !Array.isArray(orgTenants)
    ) {
      dispatch(fetchOrgTenantsStart());
    }
  }, [user, orgTenants, dispatch]);

  useComponentMount(() => {
    return () => {
      dispatch(clearSelectedMember());
    };
  });

  const isLoading: boolean = useSelector(selectMemberLoading);
  return (
    <>
      {isNewRecordedAdded && (
        <div className="add-member-inline-toast-container">
          <InlineNotification
            className="inline-toast-container"
            key={'success'}
            kind={'success'}
            title={'Success'}
            subtitle="The new asset owner was created"
            lowContrast={true}
            hideCloseButton={false}
            onCloseButtonClick={() => dispatch(updateNewRecordAddedFlag(false))}
          />
        </div>
      )}
      {memberUpdated && (
        <div className="add-member-inline-toast-container">
          <InlineNotification
            className="inline-toast-container"
            key={'success'}
            kind={'success'}
            title={'Success'}
            subtitle="Asset owner updated"
            lowContrast={true}
            hideCloseButton={false}
            onCloseButtonClick={() =>
              dispatch(updateOrgProfileUpdateFlag(false))
            }
          />
        </div>
      )}
      {isModalOpen &&
        ReactDOM.createPortal(
          <AddMemberForm params={params} />,
          document.querySelector(`#${APP}`) || document.body
        )}
      <div className="add-member-main-container container--wrapper" ref={ref}>
        <MainHeaderContainer enableHeaderGlobalBar={true} />
        <MainContainer>
          <InlineToastAlerts />
          <div className={`add-member-body-wrapper`}>
            <section className="page-heading--wrapper">
              {errorMessage ||
                (errorMessageAddMember && (
                  <ToastNotification
                    lowContrast
                    caption=""
                    iconDescription="Error fetching api"
                    title={errorMessage || errorMessageAddMember}
                    onCloseButtonClick={handleRemoveError}
                  />
                ))}

              <h1 className="main-heading">Manage asset owners</h1>
              <p className="main-description">
                {`Create a new asset owner or update an existing one's details`}
              </p>
            </section>
            <section className="add-member-content--wrapper">
              <DisplayMembers
                responseMembers={membersWithSelectedFields}
                pageInfo={pageInfo}
                setApiError={() => {}}
                handleChangeSearchBy={handleChangeSearchBy}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
                params={params}
              />
            </section>
          </div>
        </MainContainer>
        {isLoading && <Loading withOverlay={true} />}
      </div>
    </>
  );
};

Members.displayName = 'MemberModule';

export default Members;
